// ControlRecuentoModal.jsx
import { useState } from 'react';
import * as XLSX from 'xlsx';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Select,
    MenuItem,
    CircularProgress,
    Box,
    Tooltip,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { UrlApi } from '../../services/apirest';

const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// ancho fijo de la columna sucursal, se usa en varios lugares
const COL_SUCURSAL_WIDTH = 150;

export default function ControlRecuentoModal() {
    const hoy = new Date();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [anio, setAnio] = useState(hoy.getFullYear());
    const [mes, setMes] = useState(hoy.getMonth() + 1); // 1-12
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    // arma "Lun 1", "Mar 2", etc. usando el año/mes actualmente cargado en data
    const etiquetaDia = (dia) => {
        const nombreDia = DIAS_SEMANA[new Date(data.anio, data.mes - 1, dia).getDay()];
        return `${nombreDia} ${dia}`;
    };

    const cargarDatos = async (anioSel, mesSel) => {
        setLoading(true);
        setError(null);
        try {
            const resp = await fetch(`${UrlApi}/stkfisicototal/control-recuento/${anioSel}/${mesSel}`);
            if (!resp.ok) throw new Error('Error al consultar el servidor');
            const json = await resp.json();
            setData(json);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = () => {
        setOpen(true);
        cargarDatos(anio, mes);
    };

    const handleClose = () => setOpen(false);

    const handleAnioChange = (nuevoAnio) => {
        setAnio(nuevoAnio);
        cargarDatos(nuevoAnio, mes);
    };

    const handleMesChange = (nuevoMes) => {
        setMes(nuevoMes);
        cargarDatos(anio, nuevoMes);
    };

    const exportarExcel = () => {
        if (!data || !Array.isArray(data.matriz)) return;

        const headers = [
            'Sucursal',
            ...Array.from({ length: data.diasEnMes }, (_, i) => etiquetaDia(i + 1)),
        ];

        const filas = data.matriz.map((fila) => [
            fila.sucursal,
            ...Array.from({ length: data.diasEnMes }, (_, i) => fila[`d${i + 1}`]),
        ]);

        const ws = XLSX.utils.aoa_to_sheet([headers, ...filas]);

        ws['!cols'] = [
            { wch: 20 },
            ...Array.from({ length: data.diasEnMes }, () => ({ wch: 16 })),
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Control Recuento');

        const nombreArchivo = `control-recuento_${data.anio}-${String(data.mes).padStart(2, '0')}.xlsx`;
        XLSX.writeFile(wb, nombreArchivo);
    };

    const anios = [hoy.getFullYear(), hoy.getFullYear() - 1];

    // celda de sucursal, fija a la izquierda (header y body comparten estilos base)
    const stickyColSx = (isHeader) => ({
        position: 'sticky',
        left: 0,
        zIndex: isHeader ? 3 : 1,
        backgroundColor: isHeader ? '#f5f5f5' : '#fff',
        minWidth: COL_SUCURSAL_WIDTH,
        maxWidth: COL_SUCURSAL_WIDTH,
        fontWeight: isHeader ? 'bold' : 500,
        whiteSpace: 'nowrap',
        borderRight: '1px solid rgba(224, 224, 224, 1)',
    });

    return (
        <>
            <Tooltip title="Control de recuento físico por sucursal">
                <IconButton color="primary" onClick={handleOpen}>
                    <FactCheckIcon />
                </IconButton>
            </Tooltip>

            <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    Control mensual de recuento físico

                    <Select
                        size="small"
                        value={mes}
                        onChange={(e) => handleMesChange(e.target.value)}
                    >
                        {MESES.map((nombre, idx) => (
                            <MenuItem key={idx + 1} value={idx + 1}>{nombre}</MenuItem>
                        ))}
                    </Select>

                    <Select
                        size="small"
                        value={anio}
                        onChange={(e) => handleAnioChange(e.target.value)}
                    >
                        {anios.map((a) => (
                            <MenuItem key={a} value={a}>{a}</MenuItem>
                        ))}
                    </Select>

                    <IconButton
                        onClick={handleClose}
                        sx={{ marginLeft: 'auto' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {loading && (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!loading && error && (
                        <Box p={2} color="error.main">{error}</Box>
                    )}

                    {!loading && !error && data && Array.isArray(data.matriz) && (
                        <TableContainer
                            component={Paper}
                            sx={{ maxHeight: '65vh', overflow: 'auto' }}
                        >
                            <Table size="small" stickyHeader sx={{ borderCollapse: 'separate' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={stickyColSx(true)}>
                                            Sucursal
                                        </TableCell>
                                        {Array.from({ length: data.diasEnMes }, (_, i) => i + 1).map((dia) => (
                                            <TableCell key={dia} align="left" sx={{ fontWeight: 'bold' }}>
                                                {etiquetaDia(dia)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.matriz.map((fila) => (
                                        <TableRow key={fila.sucursal_id}>
                                            <TableCell sx={stickyColSx(false)}>
                                                {fila.sucursal}
                                            </TableCell>
                                            {Array.from({ length: data.diasEnMes }, (_, i) => i + 1).map((dia) => {
                                                const valor = fila[`d${dia}`];
                                                const sinDato = valor === 'Sin Dato';
                                                return (
                                                    <TableCell
                                                        key={dia}
                                                        align="left"
                                                        sx={{
                                                            whiteSpace: 'nowrap',
                                                            fontSize: '0.75rem',
                                                            color: sinDato ? 'error.main' : 'text.primary',
                                                            fontWeight: sinDato ? 'bold' : 'normal',
                                                            backgroundColor: sinDato ? 'error.lighter' : 'inherit',
                                                        }}
                                                    >
                                                        {valor}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {!loading && !error && data && !Array.isArray(data.matriz) && (
                        <Box p={2} color="warning.main">
                            Respuesta inesperada del servidor: {JSON.stringify(data)}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button
                        startIcon={<FileDownloadIcon />}
                        onClick={exportarExcel}
                        disabled={!data || loading}
                    >
                        Descargar Excel
                    </Button>
                    <Button onClick={handleClose}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}